/* ==========================================================================
   Therapium — behaviour for the th-* sections.

   The cart is ours now: sections/th-cart-drawer.liquid replaces Horizon's
   drawer and is re-rendered server-side through the Section Rendering API
   after every mutation (docs/cart-drawer-notes.md). There is no client-side
   cart model — money formatting, discounts, selling-plan names, counts and the
   free-shipping bar all come from Liquid and cannot drift from the real cart.

   Binding discipline: every delegated listener is registered once, here at
   module scope. `shopify:section:load` re-runs only initQuiz/initProduct, and
   those are guarded by a WeakSet — re-binding them on every editor reload used
   to double-bind the product form and fire two POST /cart/add.js per click.

   Public data- hooks other sections depend on are unchanged:
     data-th-cart-open, data-th-cart-count, data-th-menu-toggle, data-th-tab,
     data-th-tab-panel, data-th-tabs, data-th-add, data-th-buy, data-th-qty,
     data-th-qty-input, data-th-variant-input, data-th-variant-select,
     data-th-selling-plan-input, data-th-plan, data-th-plan-label,
     data-th-line-total, data-th-thumb, data-th-main-image, data-th-quiz*,
     data-th-motion-toggle, data-th-toggle-text.
   ========================================================================== */

(function () {
  'use strict';

  function headerOffset() {
    var height = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--th-header-height'));
    return height > 0 ? height + 16 : 90;
  }
  var DRAWER_SECTION = 'th-cart-drawer';

  /* The drawer renders its line-item thumbnails as tabindex="-1" links, so
     -1 has to be excluded everywhere or the trap would stop on them. */
  var FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var node = event.target;
      /* Text nodes and `document` have no closest(). */
      if (!node || typeof node.closest !== 'function') return;
      var target = node.closest(selector);
      if (target) handler(event, target);
    });
  }

  /* -------------------------------------------------------- motion ------- */

  /**
   * The stylesheet neutralises animations under prefers-reduced-motion but has
   * no reach into scripted scrolling, which is exactly the motion most likely
   * to trigger vestibular symptoms. Every scripted scroll goes through here.
   * Read at call time — the setting can change while the page is open.
   */
  function scrollBehavior() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'auto';
    }
    return 'smooth';
  }

  /* --------------------------------------------------- live region ------- */

  var alertNode = null;
  var alertTimer = 0;

  function alertRegion() {
    if (alertNode && document.body.contains(alertNode)) return alertNode;
    alertNode = document.createElement('div');
    alertNode.className = 'th-alert';
    alertNode.setAttribute('role', 'alert');
    alertNode.setAttribute('aria-live', 'assertive');
    alertNode.hidden = true;
    document.body.appendChild(alertNode);
    return alertNode;
  }

  /**
   * Replaces window.alert(), which blocks the page and is not announced as an
   * error. Messages come from Shopify's own API responses, so they arrive in
   * the customer's locale — nothing user-visible is hardcoded here.
   */
  function showError(message) {
    if (!message) return;
    var node = alertRegion();
    node.textContent = '';
    node.hidden = false;
    /* A live region only announces changes made while it is rendered. */
    window.requestAnimationFrame(function () { node.textContent = message; });
    window.clearTimeout(alertTimer);
    alertTimer = window.setTimeout(function () { node.hidden = true; }, 6000);
  }

  /* ------------------------------------------------------ busy state ----- */

  /**
   * `button.disabled = true` on the button the customer just pressed moves
   * focus to <body>, dropping keyboard and screen-reader users out of the page
   * on every add-to-cart. aria-busy plus the pointer-events rule in
   * therapium.css blocks the pointer without touching the focus ring.
   */
  function setBusy(el, busy) {
    if (!el) return;
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  function isBusy(el) {
    return !!el && el.getAttribute('aria-busy') === 'true';
  }

  /* ------------------------------------------------------------ cart ----- */

  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function drawerRoot() { return document.querySelector('[data-th-drawer-root]'); }
  function drawerPanel() { return document.querySelector('[data-th-drawer]'); }
  function drawerScrim() { return document.querySelector('[data-th-drawer-scrim]'); }

  function drawerIsOpen() {
    var panel = drawerPanel();
    return !!panel && !panel.hasAttribute('hidden');
  }

  var returnFocusTo = null;

  /**
   * Opens the drawer. Returns false when it is not on the page — the merchant
   * set the cart type to "page" — so the [data-th-cart-open] trigger falls
   * through to its real /cart href.
   */
  function openCartDrawer() {
    var panel = drawerPanel();
    var scrim = drawerScrim();
    if (!panel) return false;

    if (!drawerIsOpen()) {
      var active = document.activeElement;
      if (active && active !== document.body && !panel.contains(active)) returnFocusTo = active;
    }

    if (scrim) scrim.removeAttribute('hidden');
    panel.removeAttribute('hidden');
    document.documentElement.classList.add('th-drawer-open');
    panel.focus();
    return true;
  }

  function closeCartDrawer() {
    var panel = drawerPanel();
    var scrim = drawerScrim();
    if (scrim) scrim.setAttribute('hidden', '');
    if (panel) panel.setAttribute('hidden', '');
    document.documentElement.classList.remove('th-drawer-open');

    if (returnFocusTo && document.contains(returnFocusTo)) returnFocusTo.focus();
    returnFocusTo = null;
  }

  function updateCounts(value) {
    if (value === null || value === undefined) return;
    each(document.querySelectorAll('[data-th-cart-count]'), function (node) {
      node.textContent = value;
    });
  }

  /** Cart type is "page": no drawer to swap, but the header bubble still moves. */
  function refreshCount() {
    return fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCounts(cart.item_count); })
      .catch(function () { /* count stays as rendered */ });
  }

  /**
   * The one place the drawer's markup changes. Everything else calls this.
   * Fresh markup always comes back closed, so an open drawer is re-opened.
   */
  function swapDrawer(html, keepOpen) {
    var current = drawerRoot();
    if (!current) { refreshCount(); return; }

    var next = null;
    if (html) {
      next = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-th-drawer-root]');
    }

    /* The cart type was switched to "page", or the section rendered nothing. */
    if (!next) {
      current.remove();
      document.documentElement.classList.remove('th-drawer-open');
      refreshCount();
      return;
    }

    var panel = current.querySelector('[data-th-drawer]');
    var wasOpen = panel && !panel.hasAttribute('hidden');

    current.replaceWith(next);

    /* The header bubble is rendered by th-cart-pill, outside the swapped root. */
    updateCounts(next.getAttribute('data-cart-count'));

    if (keepOpen || wasOpen) openCartDrawer();
    else document.documentElement.classList.remove('th-drawer-open');
  }

  /** GET fallback, for responses without a `sections` key and for third parties. */
  function refreshDrawer(keepOpen) {
    if (!drawerRoot()) return refreshCount();
    return fetch(routeRoot() + '?sections=' + DRAWER_SECTION, {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { swapDrawer(data && data[DRAWER_SECTION], keepOpen); })
      .catch(function () { /* drawer stays as rendered */ });
  }

  function swapFromResponse(data, keepOpen) {
    var html = data && data.sections && data.sections[DRAWER_SECTION];
    if (html) swapDrawer(html, keepOpen);
    else refreshDrawer(keepOpen);
  }

  /**
   * add.js and change.js both accept `sections`, so the mutation and the
   * re-render are one round trip.
   */
  function cartPost(path, body) {
    body.sections = DRAWER_SECTION;
    body.sections_url = window.location.pathname;
    return fetch(routeRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || response.statusText);
        return data;
      });
    });
  }

  /**
   * Horizon apps and Shopify's storefront actions listen for this. `source`
   * marks our own mutations so the listener below does not fire a second,
   * redundant refresh for a swap that has already happened.
   */
  function announceCartUpdate(action) {
    document.dispatchEvent(new CustomEvent('cart:lines-update', {
      detail: { action: action, source: 'therapium' }
    }));
  }

  on('click', '[data-th-cart-open]', function (event) {
    if (openCartDrawer()) event.preventDefault();
  });

  on('click', '[data-th-drawer-close]', function (event) {
    event.preventDefault();
    closeCartDrawer();
  });

  on('click', '[data-th-drawer-scrim]', function () {
    closeCartDrawer();
  });

  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  window.swapCartDrawer = swapDrawer;

  /* Escape closes; Tab is trapped, because aria-modal is a promise the browser
     does not keep. */
  document.addEventListener('keydown', function (event) {
    if (!drawerIsOpen()) return;
    var panel = drawerPanel();

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeCartDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetWidth || node.offsetHeight || node.getClientRects().length;
    });
    if (!nodes.length) { event.preventDefault(); panel.focus(); return; }

    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /**
   * Remove a line. By 1-based line index, never by variant id: the same
   * variant can occupy several lines with different selling plans or line
   * properties. The row is only marked busy — the swap is the only thing
   * allowed to change what the drawer shows.
   */
  on('click', '[data-th-cart-remove]', function (event, link) {
    event.preventDefault();
    if (isBusy(link)) return;

    var line = parseInt(link.getAttribute('data-th-cart-remove'), 10);
    if (!line) return;

    var root = drawerRoot();
    var row = root && root.querySelector('[data-th-drawer-line="' + line + '"]');
    if (row) row.style.opacity = '0.45';
    if (root) root.setAttribute('aria-busy', 'true');
    setBusy(link, true);

    cartPost('cart/change.js', { line: line, quantity: 0 })
      .then(function (data) {
        swapFromResponse(data, false);
        announceCartUpdate('remove');
      })
      .catch(function (error) {
        if (row) row.style.opacity = '';
        if (root) root.removeAttribute('aria-busy');
        setBusy(link, false);
        showError(error.message);
      });
  });

  /**
   * 1-Click Upsell Add inside Cart Drawer
   */
  on('click', '[data-th-cart-upsell-add]', function (event, button) {
    event.preventDefault();
    if (isBusy(button)) return;

    var variantId = parseInt(button.getAttribute('data-th-cart-upsell-add'), 10);
    if (!variantId) return;

    var origHtml = button.innerHTML;
    setBusy(button, true);
    button.textContent = '...';

    cartPost('cart/add.js', {
      items: [{ id: variantId, quantity: 1 }]
    })
      .then(function (data) {
        swapFromResponse(data, true);
        announceCartUpdate('add');
      })
      .catch(function (error) {
        setBusy(button, false);
        button.innerHTML = origHtml;
        showError(error.message);
      });
  });

  /**
   * Inline Quantity Stepper in Cart Drawer (+ / -)
   */
  on('click', '[data-th-cart-qty-delta]', function (event, button) {
    event.preventDefault();
    if (isBusy(button)) return;

    var line = parseInt(button.getAttribute('data-th-cart-line'), 10);
    var delta = parseInt(button.getAttribute('data-th-cart-qty-delta'), 10);
    var currentQty = parseInt(button.getAttribute('data-th-cart-qty-current'), 10);
    if (!line || isNaN(delta)) return;

    var newQty = Math.max(0, currentQty + delta);
    var root = drawerRoot();
    var row = root && root.querySelector('[data-th-drawer-line="' + line + '"]');
    if (row) row.style.opacity = '0.45';
    if (root) root.setAttribute('aria-busy', 'true');
    setBusy(button, true);

    cartPost('cart/change.js', { line: line, quantity: newQty })
      .then(function (data) {
        swapFromResponse(data, true);
        announceCartUpdate('change');
      })
      .catch(function (error) {
        if (row) row.style.opacity = '';
        if (root) root.removeAttribute('aria-busy');
        setBusy(button, false);
        showError(error.message);
      });
  });

  /**
   * Something outside this file changed the cart — a third-party app, or
   * Shopify's own storefront actions. One request refreshes the drawer and the
   * bubble together. Our own mutations are skipped: they have already swapped.
   */
  document.addEventListener('cart:lines-update', function (event) {
    if (event.detail && event.detail.source === 'therapium') return;
    refreshDrawer(false);
  });

  /* Theme editor: selecting the section in the sidebar should show it. */
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-th-drawer-root]')) {
      openCartDrawer();
    }
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-th-drawer-root]')) {
      closeCartDrawer();
    }
  });

  /* --------------------------------------------------- mobile menu ------- */

  on('click', '[data-th-menu-toggle]', function (event, button) {
    event.preventDefault();
    var panel = document.getElementById('th-nav-panel');
    if (!panel) return;
    var open = panel.hasAttribute('hidden');
    if (open) panel.removeAttribute('hidden');
    else panel.setAttribute('hidden', '');
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* Any navigation closes the panel. */
  on('click', '#th-nav-panel a, #th-nav-panel button', function () {
    var panel = document.getElementById('th-nav-panel');
    var toggle = document.querySelector('[data-th-menu-toggle]');
    if (panel) panel.setAttribute('hidden', '');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  });

  /* ------------------------------------------------- anchor scrolling ---- */

  on('click', 'a[href*="#"]', function (event, link) {
    var url;
    try { url = new URL(link.href, window.location.href); } catch (e) { return; }
    if (url.pathname !== window.location.pathname || !url.hash || url.hash === '#') return;

    var target = document.getElementById(url.hash.slice(1));
    if (!target) return;

    event.preventDefault();
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - headerOffset(),
      behavior: scrollBehavior()
    });
    history.pushState(null, '', url.hash);

    /* preventDefault() also cancels the browser's own focus move, so a
       keyboard user who activated Horizon's "#MainContent" skip link scrolled
       and then tabbed straight back into the header (WCAG 2.4.1). Make the
       target focusable and move focus there without a second scroll. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  });

  /* Landing on /#anchor from another page. Always instant: it happens before
     the customer has looked at anything. */
  if (window.location.hash) {
    var landing = document.getElementById(window.location.hash.slice(1));
    if (landing) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          window.scrollTo({
            top: landing.getBoundingClientRect().top + window.scrollY - headerOffset(),
            behavior: 'auto'
          });
        });
      });
    }
  }

  /* --------------------------------------------------------- tabs -------- */

  /**
   * Roving tabindex, shared by the product tabs and the plan radiogroup
   * (WAI-ARIA APG: one Tab stop per composite; Left/Right/Home/End move
   * inside it and, for these two widgets, also activate). `activate` gets the
   * item that becomes current and is responsible for the tabindex swap.
   */
  function roveKey(event, items, current, activate) {
    var index = items.indexOf(current);
    if (index === -1 || items.length < 2) return;
    var next;
    switch (event.key) {
      case 'ArrowRight': case 'ArrowDown': case 'Right': case 'Down':
        next = (index + 1) % items.length; break;
      case 'ArrowLeft': case 'ArrowUp': case 'Left': case 'Up':
        next = (index - 1 + items.length) % items.length; break;
      case 'Home': next = 0; break;
      case 'End': next = items.length - 1; break;
      default: return;
    }
    event.preventDefault();
    items[next].focus();
    activate(items[next]);
  }

  function activateTab(button) {
    var root = button.closest('[data-th-tabs]');
    if (!root) return;
    var name = button.getAttribute('data-th-tab');

    each(root.querySelectorAll('[data-th-tab]'), function (tab) {
      var active = tab === button;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      tab.style.color = active ? 'var(--th-ink)' : 'var(--th-muted-meta)';
      tab.style.borderBottomColor = active ? 'var(--th-accent)' : 'transparent';
    });

    each(root.querySelectorAll('[data-th-tab-panel]'), function (panel) {
      if (panel.getAttribute('data-th-tab-panel') === name) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  on('click', '[data-th-tab]', function (event, button) {
    event.preventDefault();
    activateTab(button);
  });

  on('keydown', '[data-th-tab]', function (event, button) {
    var root = button.closest('[data-th-tabs]');
    if (!root) return;
    roveKey(event, Array.prototype.slice.call(root.querySelectorAll('[data-th-tab]')), button, activateTab);
  });

  /* --------------------------------------------------------- quiz -------- */

  var bound = new WeakSet();

  function initQuiz() {
    var root = document.querySelector('[data-th-quiz]');
    if (!root || bound.has(root)) return;
    bound.add(root);

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-th-quiz-step]'));
    var results = Array.prototype.slice.call(root.querySelectorAll('[data-th-quiz-result]'));
    var answers = {};

    if (!steps.length) return;

    /* Reveal the incoming panel before hiding the outgoing one, and move focus
       to its heading.

       Order matters. The button the visitor just pressed lives inside the
       panel being hidden, so hiding first drops focus onto <body> between the
       two statements — a screen reader then announces the whole document and a
       keyboard user is thrown to the top of the page. That happened on every
       single answer.

       No scrollIntoView anywhere: focusing the heading scrolls it into view by
       itself, and html{scroll-padding-top} keeps it clear of the sticky
       header. The old code did both, and they fought. */
    function reveal(panel, moveFocus) {
      if (!panel) return;
      panel.removeAttribute('hidden');
      if (!moveFocus) return;
      var heading = panel.querySelector('[data-th-quiz-heading]');
      if (heading) heading.focus();
    }

    function showStep(index, moveFocus) {
      reveal(steps[index], moveFocus);
      steps.forEach(function (step, i) {
        if (i !== index) step.setAttribute('hidden', '');
      });
      results.forEach(function (r) { r.setAttribute('hidden', ''); });
    }

    function matches(result) {
      var goal = result.getAttribute('data-goal');
      var depth = result.getAttribute('data-depth');
      if (goal && goal !== 'any' && goal !== answers.goal) return false;
      if (depth && depth !== 'any' && depth !== answers.depth) return false;
      return true;
    }

    function showResult() {
      var chosen = results.filter(matches)[0] || results[results.length - 1];

      /* No result blocks authored, or none left after filtering: hiding the
         steps here would leave an empty 72vh pink box with no way out. Leave
         the last step on screen instead. */
      if (!chosen) return;

      reveal(chosen, true);
      steps.forEach(function (s) { s.setAttribute('hidden', ''); });
      results.forEach(function (r) {
        if (r !== chosen) r.setAttribute('hidden', '');
      });
    }

    root.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var answer = event.target.closest('[data-th-quiz-answer]');
      if (answer) {
        event.preventDefault();
        answers[answer.getAttribute('data-th-quiz-key')] = answer.getAttribute('data-th-quiz-answer');
        var index = steps.indexOf(answer.closest('[data-th-quiz-step]'));
        if (index + 1 < steps.length && index !== -1) showStep(index + 1, true);
        else showResult();
        return;
      }

      var restart = event.target.closest('[data-th-quiz-restart]');
      if (restart) {
        event.preventDefault();
        answers = {};
        showStep(0, true);
      }
    });

    /* No focus move on load — the visitor has not asked for anything yet. */
    showStep(0, false);
  }

  /* Theme editor: answers are their own blocks now, so a merchant can select
     one whose step is hidden and the editor would scroll to nothing. Reveal
     the panel that owns the selected block, without touching focus — the
     editor manages its own. */
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var panel = target.closest('[data-th-quiz-step],[data-th-quiz-result]');
    if (!panel) return;
    var root = panel.closest('[data-th-quiz]');
    if (!root) return;
    var siblings = root.querySelectorAll('[data-th-quiz-step],[data-th-quiz-result]');
    panel.removeAttribute('hidden');
    Array.prototype.forEach.call(siblings, function (p) {
      if (p !== panel) p.setAttribute('hidden', '');
    });
  });

  /* ------------------------------------------------------- quick add ----- */

  on('click', '[data-th-add]', function (event, button) {
    var id = button.getAttribute('data-variant-id');
    if (!id) return;
    event.preventDefault();
    if (isBusy(button)) return;

    var item = { id: id, quantity: parseInt(button.getAttribute('data-quantity'), 10) || 1 };
    var plan = button.getAttribute('data-selling-plan');
    if (plan) item.selling_plan = plan;

    setBusy(button, true);

    cartPost('cart/add.js', { items: [item] })
      .then(function (data) {
        swapFromResponse(data, true);
        var body = document.querySelector('[data-th-drawer-body]');
        if (body) body.scrollTop = 0;
        announceCartUpdate('add');
      })
      .catch(function (error) { showError(error.message); })
      .finally(function () {
        setBusy(button, false);
        /* pointer-events never touched focus, so nothing to restore — but if
           anything else stole it, put it back on the control pressed. */
        if (document.activeElement === document.body && document.contains(button)) button.focus();
      });
  });

  /* ------------------------------------------------------ product page --- */

  /**
   * Mirrors Shopify's `money` filter for the shop's format string. Only used
   * for the live line total, which never leaves the page — everything that
   * matters commercially is still formatted server-side.
   *
   * There is no safe default format: falling back to '${{amount}}' printed
   * $123.45 in a store that charges RON. With no format, this renders nothing
   * and the caller leaves the server-rendered price alone.
   */
  function shopMoneyFormat() {
    if (window.Shopify && typeof window.Shopify.money_format === 'string') {
      return window.Shopify.money_format;
    }
    return '';
  }

  function formatMoney(cents, format) {
    var value = parseInt(cents, 10) || 0;
    var fmt = format || shopMoneyFormat();
    if (!fmt) return '';

    /* A money format may legitimately contain markup (<span class="money">).
       This writes with textContent, which would print those tags literally. */
    fmt = fmt.replace(/<[^>]*>/g, '');

    function withDelimiters(precision, thousands, decimal) {
      var amount = (value / 100.0).toFixed(precision);
      var parts = amount.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;

    var out;
    switch (match[1]) {
      case 'amount_no_decimals': out = withDelimiters(0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelimiters(0, '.', ','); break;
      case 'amount_with_space_separator': out = withDelimiters(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = withDelimiters(0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelimiters(2, "'", '.'); break;
      default: out = withDelimiters(2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  function initProduct() {
    var form = document.querySelector('[data-th-buy]');
    if (!form || bound.has(form)) return;
    bound.add(form);

    var root = form.closest('.th-scope') || document;
    var moneyFormat = form.getAttribute('data-money-format') || '';
    var qtyInput = form.querySelector('[data-th-qty-input]');
    var planInput = form.querySelector('[data-th-selling-plan-input]');
    var variantInput = form.querySelector('[data-th-variant-input]');
    var variantSelect = form.querySelector('[data-th-variant-select]');
    var plans = Array.prototype.slice.call(form.querySelectorAll('[data-th-plan]'));

    function activePlan() {
      return plans.filter(function (p) { return p.getAttribute('aria-checked') === 'true'; })[0] || plans[0];
    }

    function quantity() {
      var n = parseInt(qtyInput ? qtyInput.value : '1', 10);
      if (isNaN(n)) n = 1;
      return Math.min(9, Math.max(1, n));
    }

    /** One-time price follows the selected variant; the plan price does not. */
    function unitPrice() {
      var plan = activePlan();
      if (plan && plan.getAttribute('data-th-plan') === 'once' && variantSelect) {
        var opt = variantSelect.options[variantSelect.selectedIndex];
        if (opt && opt.getAttribute('data-unit-price')) return parseInt(opt.getAttribute('data-unit-price'), 10);
      }
      return plan ? parseInt(plan.getAttribute('data-unit-price'), 10) || 0 : 0;
    }

    function render() {
      if (qtyInput) qtyInput.value = quantity();

      var total = formatMoney(unitPrice() * quantity(), moneyFormat);
      /* No usable format: keep whatever Liquid rendered rather than invent a
         currency symbol or blank the price. */
      if (total) {
        each(root.querySelectorAll('[data-th-line-total]'), function (node) {
          node.textContent = total;
        });
      }

      var plan = activePlan();
      var label = root.querySelector('[data-th-plan-label]');
      if (label && plan) {
        label.textContent = (plan.getAttribute('data-plan-label') || '') + ' · ' + quantity();
      }
    }

    function selectPlan(button) {
      plans.forEach(function (p) {
        var active = p === button;
        p.setAttribute('aria-checked', active ? 'true' : 'false');
        p.setAttribute('tabindex', active ? '0' : '-1');
        /* #E4E6E8 was 1.25:1 on white. This border is the only affordance
           marking the plan card as a radio, so WCAG 1.4.11 wants 3:1. */
        p.style.border = active ? '1.5px solid #C4165F' : '1px solid #8A8F94';
        p.style.background = active ? (p.getAttribute('data-active-bg') || '#FFF3F8') : '#FFFFFF';
      });
      if (planInput) planInput.value = button.getAttribute('data-selling-plan') || '';
      render();
    }

    plans.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        selectPlan(button);
      });
      /* role="radio" inside role="radiogroup": arrows move AND select. */
      button.addEventListener('keydown', function (event) {
        roveKey(event, plans, button, selectPlan);
      });
    });

    form.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;
      var step = event.target.closest('[data-th-qty]');
      if (!step || !qtyInput) return;
      event.preventDefault();
      qtyInput.value = quantity() + (parseInt(step.getAttribute('data-th-qty'), 10) || 0);
      render();
    });

    if (qtyInput) qtyInput.addEventListener('input', render);
    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        if (variantInput) variantInput.value = variantSelect.value;
        render();
      });
    }

    /* Gallery thumbnails. */
    root.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;
      var thumb = event.target.closest('[data-th-thumb]');
      if (!thumb) return;
      event.preventDefault();
      var main = root.querySelector('[data-th-main-image]');
      var src = thumb.getAttribute('data-th-image');
      if (main && src) {
        main.src = src;
        main.removeAttribute('srcset');
      }
      each(root.querySelectorAll('[data-th-thumb]'), function (t) {
        var active = t === thumb;
        t.style.border = active ? '1.5px solid #0A0A0B' : '1px solid #ECEDEE';
        if (active) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    });

    /* Add to cart through the Cart API so the drawer opens in place. */
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var id = variantInput ? variantInput.value : (variantSelect ? variantSelect.value : '');
      if (!id) return;

      var submit = form.querySelector('[type="submit"]');
      if (isBusy(submit)) return;

      var item = { id: id, quantity: quantity() };
      if (planInput && planInput.value) item.selling_plan = planInput.value;

      setBusy(submit, true);

      cartPost('cart/add.js', { items: [item] })
        .then(function (data) {
          swapFromResponse(data, true);
          var body = document.querySelector('[data-th-drawer-body]');
          if (body) body.scrollTop = 0;
          announceCartUpdate('add');
        })
        .catch(function (error) { showError(error.message); })
        .finally(function () {
          setBusy(submit, false);
          if (submit && document.activeElement === document.body && document.contains(submit)) submit.focus();
        });
    });

    render();
  }

  /* ------------------------------------------------ motion toggles ------- */

  /**
   * Visible pause/play for the hero video and the announcement marquee.
   * WCAG 2.2.2: moving content that starts automatically and lasts more than
   * five seconds needs an on-page way to pause it; prefers-reduced-motion is
   * a system setting, not a mechanism on the page.
   *
   * aria-pressed="true" means "paused". The visible label swaps between the
   * two section settings carried on the button. Video state follows the
   * element's own play/pause events, so a blocked autoplay or the
   * reduced-motion pause in th-hero shows "Play" without a second code path.
   */
  var toggleBound = new WeakSet();

  function toggleTarget(button) {
    var id = button.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setToggleState(button, paused) {
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    var label = button.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
    var text = button.querySelector('[data-th-toggle-text]') || button;
    if (label) text.textContent = label;
  }

  on('click', '[data-th-motion-toggle]', function (event, button) {
    event.preventDefault();
    var target = toggleTarget(button);
    if (!target) return;
    var paused = button.getAttribute('aria-pressed') === 'true';

    if (button.getAttribute('data-th-motion-toggle') === 'video') {
      if (paused) {
        var p = target.play();
        if (p && p.catch) p.catch(function () { /* still paused; the events keep the label honest */ });
      } else {
        target.pause();
      }
      return;
    }

    target.style.animationPlayState = paused ? 'running' : 'paused';
    setToggleState(button, !paused);
  });

  function initMotionToggles() {
    each(document.querySelectorAll('[data-th-motion-toggle="video"]'), function (button) {
      var video = toggleTarget(button);
      if (!video) return;
      setToggleState(button, video.paused);
      if (toggleBound.has(button)) return;
      toggleBound.add(button);
      ['play', 'pause'].forEach(function (type) {
        video.addEventListener(type, function () { setToggleState(button, video.paused); });
      });
    });
  }

  /* --------------------------------------------------------- boot -------- */

  /* Only the per-element initialisers run here, and each refuses to bind the
     same element twice. Everything delegated is already bound, once, above. */
  function init() { initQuiz(); initProduct(); initMotionToggles(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
